import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { ApiError, api, unwrap } from "@/client/lib/api";
import { type CompanySession, fetchTenantData } from "@/client/lib/session";
import type {
  PayDay,
  PayHistory,
  Payslip,
  PayslipDocumentData,
  PayslipMonth,
} from "@/features/payslips/schemas";
import { isReadOnly } from "@/shared/plan";

// Every key starts with "pay", which the persister skips: no figure here reaches IndexedDB.

/** A month's slips as they stand. Reading never drafts: `openMonth` does, when the month opens. */
export const monthSlipsQuery = (month: string) =>
  queryOptions({
    queryKey: ["pay", "payslips", month],
    queryFn: (): Promise<PayslipMonth> =>
      unwrap(api.payslips.$get({ query: { month } })),
  });

/** Drafts whoever the month is missing a slip for, and holds the month the server answers. */
export const draftMonth = (queryClient: QueryClient, month: string) =>
  fetchTenantData(queryClient, {
    ...monthSlipsQuery(month),
    queryFn: (): Promise<PayslipMonth> =>
      unwrap(api.payslips.drafts.$post({ json: { month } })),
    staleTime: 0,
  });

const readMonth = (queryClient: QueryClient, month: string) =>
  fetchTenantData(queryClient, { ...monthSlipsQuery(month), staleTime: 0 });

/**
 * Opening a month drafts it first, except in a read-only company, which refuses the draft and
 * opens on the slips it has. A month held and not invalidated since opens as held: a change of pay
 * invalidates every month.
 */
export function openMonth(
  queryClient: QueryClient,
  { companyPlan }: Pick<CompanySession, "companyPlan">,
  month: string,
): Promise<PayslipMonth> {
  const held = queryClient.getQueryState(monthSlipsQuery(month).queryKey);
  if (held?.data && !held.isInvalidated) return Promise.resolve(held.data);
  if (companyPlan && isReadOnly(companyPlan.state)) {
    return readMonth(queryClient, month);
  }
  // A tab opened before the plan ended still holds the plan as it was.
  return draftMonth(queryClient, month).catch((failure: unknown) => {
    if (failure instanceof ApiError && failure.code === "PLAN_ENDED") {
      return readMonth(queryClient, month);
    }
    throw failure;
  });
}

export const mySlipsQuery = queryOptions({
  queryKey: ["pay", "payslips", "mine"],
  queryFn: (): Promise<Payslip[]> =>
    unwrap(api.payslips.mine.$get()).then(({ items }) => items),
});

export const payDayQuery = queryOptions({
  queryKey: ["pay", "pay-day"],
  queryFn: (): Promise<PayDay> => unwrap(api.payslips["pay-day"].$get()),
});

export const payHistoryQuery = (personId: string) =>
  queryOptions({
    queryKey: ["pay", "setups", personId],
    queryFn: (): Promise<PayHistory> =>
      unwrap(api.people[":personId"].pay.$get({ param: { personId } })),
  });

/** A slip as it prints, beside the slip on screen. Amounts arrive as digits and become bigints. */
export const documentQuery = (id: string) =>
  queryOptions({
    queryKey: ["pay", "document", id],
    queryFn: async (): Promise<PayslipDocumentData> => {
      const read = await unwrap(
        api.payslips[":id"].document.$get({ param: { id } }),
      );
      return {
        ...read,
        lines: read.lines.map((line) => ({
          ...line,
          amountMinor: BigInt(line.amountMinor),
        })),
        grossMinor: BigInt(read.grossMinor),
        deductionsMinor: BigInt(read.deductionsMinor),
        netMinor: BigInt(read.netMinor),
      };
    },
  });
